import {Box, Typography} from '@mui/material';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import {t} from 'i18next';
import EvidenceSourceGrid from './EvidenceSourceGrid';
import {ReactComponent as BackgroundBlur} from '../../../assests/svg/backgroundBlur.svg';
import {useMemo, useState} from 'react';
import AurButton from '../../common/aur-button/AurButton';
import {ReactComponent as ExportIcon} from '../../../assests/svg/export_icon.svg';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {RootState} from '../../../redux/store';
import {useSelector} from 'react-redux';
import {
  downloadResponseAsXLSX,
  getProjectReportFileName,
} from '../../../utils/utils';
import {exportEvidenceReport} from '../../../api/EvidenceSourceUpload';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {AxiosError} from 'axios';
import {showErrorToast} from '../../../api/utils';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

/**
 * Evidence Source component
 * @returns
 */
const EvidenceSource = () => {
  /**
   * Breadcrumb items with label & path
   */
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const [isListHasData, setIsListHasData] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const {showToast} = useToast();
  const project = useSelector(
    (state: RootState) => state.uploadEvidenceSource.selectedProject
  );
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const {trackGAEvent} = useAnalytics();

  const fetchProjectEvidenceReport = async () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.EXPORT,
      file_extension: GA_FILE_EXTENTIONS.XLSX,
      click: {
        name: t('gaTracking.manageProjectEvidence.exportReport'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.manageProjectEvidence.exportedEvidenceReport'),
      },
    });
    setIsExportLoading(true);
    try {
      const paramString = `clientId=${clientId}&projectId=${project?.projectId}`;
      const response = await exportEvidenceReport(paramString);
      const fileName = getProjectReportFileName(
        `${project?.projectAlphaCode}_${project?.projectName}`,
        ''
      );
      downloadResponseAsXLSX(response, fileName);
    } catch (error: unknown) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
    } finally {
      setIsExportLoading(false);
    }
  };

  const breadCrumbItems = useMemo(() => {
    const baseItems: {label: string; path?: string}[] = [
      {
        label: t('evidenceSource.projectOverview'),
        path: '/welcome-single-client',
      },
      {
        label: t('evidenceSource.manageProjectEvidence'),
      },
    ];
    return baseItems;
  }, []);

  const renderHeaderNButtons = () => {
    return (
      <>
        <Typography
          className="typo-text display3_size evidence-source-title"
          data-testid="evidence-source-title"
        >
          {t('evidenceSource.manageProjectEvidence')}
        </Typography>
        <Box className="button-wrapper">
          <AurButton
            disabled={isRefreshing}
            onClick={() => {
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.global.refresh'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.BODY,
                  action: t(
                    'gaTracking.manageProjectEvidence.refreshedEvidenceReport'
                  ),
                },
              });
              setIsRefreshing(true);
              setRefreshTrigger((prev) => prev + 1);
            }}
            startIcon={
              isRefreshing ? (
                <div className="color-loader">
                  <AurLoadingIndicator
                    size={16}
                    className="progress-icon"
                    aria-hidden="true"
                  />
                </div>
              ) : (
                <AurIcon
                  icon={IconNames.CachedIcon}
                  color={IconColors.Custom}
                  customClassName="refresh-icon"
                />
              )
            }
            type="secondary"
            size="Medium"
            label={t('common.refresh')}
            dataTestId="refresh-btn"
            classNames="typo-text buttonMedium_size"
          />
          <AurButton
            disabled={!isListHasData}
            onClick={() => fetchProjectEvidenceReport()}
            startIcon={
              isExportLoading ? (
                <div className="color-loader">
                  <AurLoadingIndicator
                    size={16}
                    className="progress-icon"
                    aria-hidden="true"
                  />
                </div>
              ) : (
                <ExportIcon aria-hidden />
              )
            }
            type="continue"
            size="Medium"
            label={t('common.exportReport')}
            dataTestId="export-report-btn"
            classNames="typo-text buttonMedium_size"
          />
        </Box>
      </>
    );
  };

  return (
    <Box className="evidence-source-container">
      <BackgroundBlur aria-hidden="true" />
      <div className="evidence-source-layout">
        <Box
          className="breadcrumb-section evidence-source-breadcrumb"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <AurBreadcrumb items={breadCrumbItems} />
        </Box>

        <Typography
          data-testid="project-title"
          className="typo-text subhead3_size project-title"
        >
          {project?.projectName}
        </Typography>
        <Box display={'flex'} justifyContent={'space-between'}>
          {renderHeaderNButtons()}
        </Box>
      </div>

      <Box className="evidence-source-grid-container">
        <EvidenceSourceGrid
          clientId={clientId}
          projectId={project?.projectId}
          setListHasData={setIsListHasData}
          refreshTrigger={refreshTrigger}
          setIsRefreshing={setIsRefreshing}
        />
      </Box>
    </Box>
  );
};
export default EvidenceSource;
